import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Save, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { errorText } from "@/components/coaching/GuidedDay";

export const FilmAsk = ({ analysisIds, defaultQuestion, onSaved }) => {
  const [q, setQ] = useState(defaultQuestion), [busy, setBusy] = useState(false), [ans, setAns] = useState(null), [title, setTitle] = useState("");
  useEffect(() => { setQ(defaultQuestion); setAns(null); }, [defaultQuestion, analysisIds.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps
  const go = async (save) => {
    setBusy(true);
    try { const r = await api.post("/squad/media/ask", { analysis_ids: analysisIds, question: q, save, title }, { timeout: 180000 }); setAns(r.data); if (save) { toast.success("Report saved"); onSaved?.(); } }
    catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };
  return (
    <section className="border border-foreground p-4 space-y-3" data-testid="film-ask">
      <h3 className="font-heading font-bold flex items-center gap-2"><Sparkles size={16} />{`Ask AI · ${analysisIds.length} film${analysisIds.length > 1 ? "s" : ""}`}</h3>
      <textarea data-testid="film-ask-question" className="inp" rows={2} value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="flex flex-wrap gap-2 items-center">
        <Button data-testid="film-ask-go" disabled={busy || q.trim().length < 3} onClick={() => go(false)}>{busy ? "Thinking…" : "Ask"}</Button>
        <input data-testid="film-ask-title" className="inp w-56 py-1.5" placeholder="Report name (optional)" value={title} onChange={(e) => setTitle(e.target.value)} />
        <Button variant="outline" data-testid="film-ask-save" disabled={busy || q.trim().length < 3} onClick={() => go(true)}><Save size={14} />Ask & save report</Button>
        <span className="text-[11px] text-muted-foreground">Charged by usage</span>
      </div>
      {ans && <div data-testid="film-ask-answer" className="text-sm whitespace-pre-wrap border-t border-border pt-3">{ans.answer}</div>}
    </section>
  );
};

export const FilmReports = ({ squad, athleteId, reloadKey }) => {
  const [list, setList] = useState([]), [open, setOpen] = useState("");
  const load = useCallback(() => api.get("/squad/media/reports", { params: { squad, athlete_id: athleteId } }).then((r) => setList(r.data.reports)).catch(() => setList([])), [squad, athleteId]);
  useEffect(() => { load(); }, [load, reloadKey]);
  if (!list.length) return null;
  return (
    <section className="border border-border" data-testid="film-reports">
      <h3 className="px-4 py-3 border-b border-border font-heading font-bold">Saved reports</h3>
      {list.map((r) => (
        <div key={r.report_id} className="border-b border-border last:border-0 px-4 py-3" data-testid={`film-report-${r.report_id}`}>
          <div className="flex items-center gap-2">
            <button data-testid={`film-report-open-${r.report_id}`} className="text-left flex-1 text-sm font-medium" onClick={() => setOpen(open === r.report_id ? "" : r.report_id)}>{r.title}<span className="block text-[11px] text-muted-foreground font-normal">{`${r.files.join(", ")} · ${r.at.slice(0, 10)}`}</span></button>
            <button aria-label="Delete report" data-testid={`film-report-del-${r.report_id}`} onClick={() => api.delete(`/squad/media/reports/${r.report_id}`).then(load)}><Trash2 size={14} /></button>
          </div>
          {open === r.report_id && <p className="text-sm whitespace-pre-wrap mt-2" data-testid={`film-report-body-${r.report_id}`}>{r.answer}</p>}
        </div>
      ))}
    </section>
  );
};
